/**
 * Slider settings helper — CFSN-510.
 *
 * Reads global slider settings (rendered as data-* attributes by
 * snippets/slider-settings.liquid) from any wrapper element and turns them
 * into a normalized config object + a Flickity options object.
 *
 * Exposes:
 *   window.getSliderSettings(el) → { dragDesktop, swipeMobile, mobileMode,
 *     autoGroup, stepDesktop, stepTablet, stepMobile, showArrows, showDots,
 *     transitionDuration, mobileFriction }
 *   window.getFlickityOptions(el, baseOptions, overrides) → Flickity options for
 *     the *current* breakpoint (mobile ≤749, tablet 750–1023, desktop ≥1024).
 *   window.SliderSettings.isMobile() / isTablet() / isDesktop() → boolean
 *   window.SliderSettings.onBreakpointChange(callback) → returns cleanup fn.
 *     Fires when crossing the 749px or 1023px boundary.
 */
(function () {
  if (window.getSliderSettings) return;

  var DEFAULTS = {
    dragDesktop: true,
    swipeMobile: true,
    mobileMode: 'free',
    autoGroup: true,
    stepDesktop: 1,
    stepTablet: 3,
    stepMobile: 1,
    showArrows: true,
    showDots: true,
    transitionDuration: 400,
    mobileFriction: 0.2,
  };

  function asBool(v, def) {
    if (v === 'true' || v === true) return true;
    if (v === 'false' || v === false) return false;
    return def;
  }

  function asInt(v, def) {
    var n = parseInt(v, 10);
    return isNaN(n) ? def : n;
  }

  function asFloat(v, def) {
    var n = parseFloat(v);
    return isNaN(n) ? def : n;
  }

  var MOBILE_MAX = 749;
  var TABLET_MAX = 1023;

  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;
  }
  function isTablet() {
    return window.matchMedia('(min-width: ' + (MOBILE_MAX + 1) + 'px) and (max-width: ' + TABLET_MAX + 'px)').matches;
  }
  function isDesktop() {
    return window.matchMedia('(min-width: ' + (TABLET_MAX + 1) + 'px)').matches;
  }

  function onBreakpointChange(callback) {
    var mqMobile = window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)');
    var mqTablet = window.matchMedia('(max-width: ' + TABLET_MAX + 'px)');
    mqMobile.addEventListener('change', callback);
    mqTablet.addEventListener('change', callback);
    return function () {
      mqMobile.removeEventListener('change', callback);
      mqTablet.removeEventListener('change', callback);
    };
  }

  function getSliderSettings(el) {
    if (!el || !el.dataset) return Object.assign({}, DEFAULTS);
    var d = el.dataset;
    return {
      dragDesktop: asBool(d.sliderDragDesktop, DEFAULTS.dragDesktop),
      swipeMobile: asBool(d.sliderSwipeMobile, DEFAULTS.swipeMobile),
      mobileMode: d.sliderMobileMode === 'step' ? 'step' : 'free',
      autoGroup: asBool(d.sliderAutoGroup, DEFAULTS.autoGroup),
      stepDesktop: asInt(d.sliderStepDesktop, DEFAULTS.stepDesktop),
      stepTablet: asInt(d.sliderStepTablet, DEFAULTS.stepTablet),
      stepMobile: asInt(d.sliderStepMobile, DEFAULTS.stepMobile),
      showArrows: asBool(d.sliderShowArrows, DEFAULTS.showArrows),
      showDots: asBool(d.sliderShowDots, DEFAULTS.showDots),
      transitionDuration: asInt(d.sliderTransition, DEFAULTS.transitionDuration),
      mobileFriction: asFloat(d.sliderMobileFriction, DEFAULTS.mobileFriction),
    };
  }

  // Map transition_duration (ms) to Flickity's spring physics.
  // Flickity has no direct duration option; selectedAttraction (higher = faster)
  // shapes the slide animation. Tuned so 200ms ≈ snappy, 400ms ≈ default,
  // 800ms ≈ relaxed.
  function attractionForDuration(ms) {
    if (ms <= 200) return 0.04;
    if (ms <= 400) return 0.025;
    if (ms <= 600) return 0.018;
    if (ms <= 800) return 0.012;
    return 0.008;
  }

  function getFlickityOptions(el, baseOptions, overrides) {
    var s = getSliderSettings(el);
    var mobile = isMobile();
    var tablet = isTablet();

    var perStep = mobile ? s.stepMobile : tablet ? s.stepTablet : s.stepDesktop;
    var freeScroll = mobile && s.mobileMode === 'free';
    // Auto-grouping uses Flickity's `groupCells: true` so the slider snaps by
    // however many cards currently fit. It also avoids a known boundary desync
    // (selectedIndex outruns `contain` clamping) that `groupCells: 1` triggers.
    // Manual mode passes the merchant-configured per-breakpoint integer.
    var groupCells = freeScroll ? false : (s.autoGroup ? true : perStep);

    var opts = {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: s.showDots,
      prevNextButtons: s.showArrows,
      groupCells: groupCells,
      freeScroll: freeScroll,
      draggable: mobile ? s.swipeMobile : s.dragDesktop,
      selectedAttraction: attractionForDuration(s.transitionDuration),
      friction: mobile ? s.mobileFriction : 0.28,
    };

    if (baseOptions) {
      for (var k in baseOptions) opts[k] = baseOptions[k];
    }
    if (overrides) {
      for (var k2 in overrides) opts[k2] = overrides[k2];
    }
    return opts;
  }

  window.getSliderSettings = getSliderSettings;
  window.getFlickityOptions = getFlickityOptions;
  window.SliderSettings = {
    isMobile: isMobile,
    isTablet: isTablet,
    isDesktop: isDesktop,
    onBreakpointChange: onBreakpointChange,
  };
})();
